import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch } from "../../../hooks/redux-hooks";
import AuthUser from "../../../models/authUser/authUser";
import BaseService from "../../../service/base.service";
import notify from "../../../components/common/notificationHelper";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../slices/authSlice";
import {
  Form,
  Input,
  Select,
  Button,
  Avatar,
  Typography,
  Modal,
  Skeleton, Card, Upload
} from "antd";
import { EyeInvisibleOutlined, EyeTwoTone, SaveOutlined, CloseOutlined, EditOutlined, CameraOutlined } from "@ant-design/icons";
import { useIsMobile } from "../../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { Option } = Select;

const GENDERS = ["Male", "Female"];

interface AuthUserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  academicTitle: string;
  gender: string;
  city: string;
  country: string;
  phoneNumber: string;
}

const ProfileCard: React.FC = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState<AuthUser>();
  const [form] = Form.useForm<AuthUserFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [photo, setPhoto] = useState<string>("");
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordFormHasErrors, setPasswordFormHasErrors] = useState(true);
  const [passwordForm] = Form.useForm();

  const [loading, setLoading] = useState(true); // loading state

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      handleGetAuthUser();
    }
  }, []);

  const handleGetAuthUser = async () => {
    try {
      const rp = await BaseService.get<AuthUser>("/auth/users", null);
      if (rp.Status) {
        form.setFieldsValue({
          ...rp.Data,
        });
        setUserDetails(rp.Data);
        setPhoto(rp.Data.photoUrl || "");
        setLoading(false); // finished loading
      } else {
        console.error("Status error:", rp.Messages, rp.Exception);
        setLoading(false);
      }
    } catch (err) {
      console.error("Fetch user failed:", err);
      setLoading(false);
    }
  };

  const onFinish = async (values: AuthUserFormValues) => {
    try {
      BaseService.update("/auth/users", null, values).then((rp) => {
        if (rp.Status) {
          notify({
            title: "Success",
            description: `User Details has been updated.`,
            variant: "success",
          });
          handleGetAuthUser();
          setIsEditing(false);
        } else {
          notify({
            title: "Error",
            description: `Failed to update User Details.`,
            variant: "error",
          });
          console.log("Messages: " + rp.Messages);
          console.log("Exception: " + rp.Exception);
        }
      });
    } finally {
    }
  };

  const showPasswordModal = () => {
    setPasswordModalVisible(true);
    passwordForm.resetFields();
    setPasswordFormHasErrors(true);
  };

  const hidePasswordModal = () => {
    setPasswordModalVisible(false);
  };


  const handleCancel = () => {
    setIsEditing(false);
  };

  const onPasswordFinish = (values: any) => {
    const payload = {
      oldPassword: values.currentPassword,
      newPassword: values.newPassword,
    };
    BaseService.update("/auth/users/change-password", null, payload).then(
      (rp) => {
        if (rp.Status) {
          setPasswordModalVisible(false);
          handleLogout();
        } else {
          console.log("Messages: " + rp.Messages);
          console.log("Exception: " + rp.Exception);
        }
      }
    );
  };

  const handleUploadPhoto = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    console.log("Uploading photo:", file);
    try {
      const rp = await BaseService.upload("/auth/users/photo", formData, null);
      if (rp.Status) {
        notify({
          title: "Success",
          description: `User photo updated.`,
          variant: "success",
        });
        handleGetAuthUser();
      } else {
        notify({
          title: "Error",
          description: `Failed to update user photo.`,
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate("/login");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Card
      style={{
        width: "100%",
        borderRadius: isMobile ? 12 : 16,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        padding: isMobile ? 12 : 24,
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "center",
          marginBottom: 24,
          gap: isMobile ? 12 : 0,
          textAlign: isMobile ? "center" : "left",
        }}
      >
        <Upload showUploadList={false} beforeUpload={handleUploadPhoto}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              marginRight: isMobile ? 0 : 16,
            }}
          >
            <div style={{ position: "relative" }}>
              <Avatar
                size={isMobile ? 80 : 100}
                src={photo || undefined}
                style={{
                  backgroundColor: "#f0f0f0",
                  fontSize: 36,
                  border: "2px solid #d9d9d9",
                }}
              >
                {!photo && (
                  <>
                    {userDetails?.firstName?.charAt(0).toUpperCase()}
                    {userDetails?.lastName?.charAt(0).toUpperCase()}
                  </>
                )}
              </Avatar>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "rgba(0,0,0,0.4)",
                  color: "#fff",
                  borderRadius: "50%",
                  opacity: 0,
                  transition: "opacity 0.3s",
                }}
                className="avatar-overlay"
              >
                <CameraOutlined style={{ fontSize: 20 }} />
              </div>
            </div>
            <Text
              style={{
                marginTop: 8,
                color: "#fa8c16",
                fontWeight: 500,
                border: "1px solid #fa8c16",
                padding: "2px 8px",
                borderRadius: 4,
              }}
            >
              <CameraOutlined style={{ marginRight: 4 }} />
              Upload Picture
            </Text>
          </div>
        </Upload>


        <div style={{ flex: 1, minWidth: 0, width: isMobile ? "100%" : undefined }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: isMobile ? "center" : "flex-start" }}>
              {/* Skeleton for firstName + lastName */}
              <Skeleton.Input
                active
                size="small"
                style={{ width: 180, height: 28, marginBottom: 4 }}
              />
              {/* Skeleton for email */}
              <Skeleton.Input
                active
                size="small"
                style={{ width: 240, height: 20 }}
              />
            </div>
          ) : (
            <>
              <Title level={4} style={{ margin: 0, fontSize: isMobile ? 18 : undefined }}>
                {`${form.getFieldValue("firstName") || "-"} ${form.getFieldValue("lastName") || "-"
                  }`}
              </Title>
              <Text type="secondary" style={{ display: "block" }}>
                {form.getFieldValue("email") || "-"}
              </Text>
            </>
          )}
        </div>

        {!isEditing ? (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
            block={isMobile}
          >
            Edit
          </Button>
        ) : (
          <div
            style={{
              display: "flex",
              gap: 8,
              width: isMobile ? "100%" : undefined,
              justifyContent: isMobile ? "stretch" : undefined,
            }}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              block={isMobile}
            >
              Save
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancel}
              block={isMobile}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Profile Form */}
      <Form form={form} layout="vertical" onFinish={onFinish} disabled={!isEditing}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Form.Item
            label="Academic Title"
            name="academicTitle"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
          >
            <Input />
          </Form.Item>
          {!isMobile && (
            <div style={{ flex: "1 1 calc(50% - 8px)" }} />
          )}
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Form.Item
            label="First Name"
            name="firstName"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
            rules={[{ required: true, message: "Please enter your first name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
            rules={[{ required: true, message: "Please enter your last name" }]}
          >
            <Input />
          </Form.Item>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Form.Item
            label="Gender"
            name="gender"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
          >
            <Select placeholder="Select Gender" allowClear>
              {GENDERS.map((g) => (
                <Option key={g} value={g}>
                  {g}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
          >
            <Input />
          </Form.Item>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Form.Item
            label="Country"
            name="country"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="City"
            name="city"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 calc(50% - 8px)" }}
          >
            <Input />
          </Form.Item>
        </div>
      </Form>

      {/* Change Password Button */}
      <div style={{ textAlign: isMobile ? "center" : "right", marginTop: 20 }}>
        <Button type="default" onClick={showPasswordModal} block={isMobile}>
          Change Password
        </Button>
      </div>

      {/* Password Modal */}
      <Modal
        title="Change Password"
        visible={passwordModalVisible}
        onCancel={hidePasswordModal}
        footer={null}
        destroyOnClose
        width={isMobile ? "calc(100vw - 24px)" : undefined}
        centered
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={onPasswordFinish}
          preserve={false}
          onFieldsChange={() => {
            const hasErrors = passwordForm
              .getFieldsError()
              .some((field) => field.errors.length > 0);
            const values = passwordForm.getFieldsValue();
            const passwordsMatch = values.newPassword === values.confirmPassword;
            const allFieldsFilled =
              values.currentPassword && values.newPassword && values.confirmPassword;
            setPasswordFormHasErrors(hasErrors || !passwordsMatch || !allFieldsFilled);
          }}
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: "Please enter your current password" }]}
          >
            <Input.Password
              placeholder="Current Password"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter your new password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password
              placeholder="New Password"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            hasFeedback
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm New Password"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={passwordFormHasErrors}
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProfileCard;
